import axios from 'axios';

const API_URL = process.env.VUE_APP_API_URL;

class Provider {
  async dailyStats() {
    try {
      const  {data}  =  await axios.get(API_URL + '/entities/stats/byday');
      return data.data; 
    } catch(error) {
      if (error.response.status == 429) {
        return { status: "ratelimited", data: {} }
      } else if (error.response.status == 401) {
        return { status: "unauthorized", data: {} }
      } else {
        return { status: "failed", data: {} }
      }
    }
  }

  async typeStats() {
    try {
      const  {data}  =  await axios.get(API_URL + '/entities/stats/type');
      return data.data; 
    } catch(error) {
      if (error.response.status == 429) {
        return { status: "ratelimited", data: {} }
      } else if (error.response.status == 401) {
        return { status: "unauthorized", data: {} }
      } else {
        return { status: "failed", data: {} }
      }
    }
  }

  async providerStats() {
    try {
      const  {data}  =  await axios.get(API_URL + '/entities/stats/provider');
      return data.data;
    } catch(error) {
      if (error.response.status == 429) {
        return { status: "ratelimited", data: {} }
      } else if (error.response.status == 401) {
        return { status: "unauthorized", data: {} }
      } else {
        return { status: "failed", data: {} }
      }
    }
  }

  async getProvider(name) {
    try {
      const { data } = await axios.get(`${API_URL}/entities/providers/${name}`);
      return data.data;
    } catch(error) {
      if (error.response.status == 429) {
        return { status: "ratelimited", data: {} }
      } else if (error.response.status == 401) {
        return { status: "unauthorized", data: {} }
      } else if (error.response.status == 404) {
        return { status: "not_found", data: {} }
      } else {
        return { status: "failed", data: {} }
      }
    }
  }

  async listProviders() {
    try {
      const { data } = await axios.get(`${API_URL}/entities/providers`);
      return data.data || [];
    } catch(error) {
      if (error.response?.status == 429) {
        return { status: "ratelimited", data: [] }
      } else if (error.response?.status == 401) {
        return { status: "unauthorized", data: [] }
      } else {
        return { status: "failed", data: [] }
      }
    }
  }

  async getMissingProviders() {
    try {
      const { data } = await axios.get(`${API_URL}/entities/providers/missing`);
      return data.data || [];
    } catch(error) {
      if (error.response?.status == 429) {
        return { status: "ratelimited", data: [] }
      } else if (error.response?.status == 401) {
        return { status: "unauthorized", data: [] }
      } else {
        return { status: "failed", data: [] }
      }
    }
  }

  async createProvider(providerData) {
    try {
      const { data } = await axios.post(`${API_URL}/entities/providers`, providerData);
      return { status: "success", data: data.data };
    } catch(error) {
      if (error.response.status == 429) {
        return { status: "ratelimited", message: "Too many requests" }
      } else if (error.response.status == 401) {
        return { status: "unauthorized", message: "Not authorized" }
      } else if (error.response.status == 409) {
        return { status: "conflict", message: "Provider with this name already exists" }
      } else if (error.response.status == 400) {
        return { status: "validation_error", message: error.response.data.message || "Invalid data" }
      } else {
        return { status: "failed", message: "Failed to create provider" }
      }
    }
  }

  async updateProvider(name, providerData) {
    try {
      const { data } = await axios.put(`${API_URL}/entities/providers/${name}`, providerData);
      return { status: "success", data: data.data };
    } catch(error) {
      if (error.response.status == 429) {
        return { status: "ratelimited", message: "Too many requests" }
      } else if (error.response.status == 401) {
        return { status: "unauthorized", message: "Not authorized" }
      } else if (error.response.status == 404) {
        return { status: "not_found", message: "Provider not found" }
      } else if (error.response.status == 400) {
        return { status: "validation_error", message: error.response.data.message || "Invalid data" }
      } else {
        return { status: "failed", message: "Failed to update provider" }
      }
    }
  }

  async getProviderGeography(name) {
    try {
      const { data } = await axios.get(`${API_URL}/entities/providers/${name}/geography`);
      return data.data;
    } catch(error) {
      console.error('Provider geography error:', error);
      return {};
    }
  }

  async uploadThumbnail(name, file) {
    try {
      const formData = new FormData();
      formData.append('thumbnail', file);

      const { data } = await axios.post(`${API_URL}/entities/providers/${name}/thumbnail`, formData);
      return { status: "success", data: data.data };
    } catch(error) {
      if (error.response.status == 429) {
        return { status: "ratelimited", message: "Too many requests" }
      } else if (error.response.status == 401) {
        return { status: "unauthorized", message: "Not authorized" }
      } else if (error.response.status == 404) {
        return { status: "not_found", message: "Provider not found" }
      } else if (error.response.status == 400) {
        return { status: "validation_error", message: error.response.data.message || "Invalid file" }
      } else {
        return { status: "failed", message: "Failed to upload thumbnail" }
      }
    }
  }

  async deleteThumbnail(name) {
    try {
      const { data } = await axios.delete(`${API_URL}/entities/providers/${name}/thumbnail`);
      return { status: "success", data: data.data };
    } catch(error) {
      if (error.response.status == 429) {
        return { status: "ratelimited", message: "Too many requests" }
      } else if (error.response.status == 401) {
        return { status: "unauthorized", message: "Not authorized" }
      } else if (error.response.status == 404) {
        return { status: "not_found", message: "Provider not found or no thumbnail to delete" }
      } else if (error.response.status == 400) {
        return { status: "validation_error", message: error.response.data.message || "Bad request" }
      } else {
        return { status: "failed", message: "Failed to delete thumbnail" }
      }
    }
  }

  async getValidCountryCodes() {
    try {
      const { data } = await axios.get(`${API_URL}/entities/providers/validation/countries`);
      return { status: "success", data: data.data };
    } catch(error) {
      console.error('Error fetching country codes:', error);
      return { status: "failed", data: [] };
    }
  }

  async getValidProviderTypes() {
    try {
      const { data } = await axios.get(`${API_URL}/entities/providers/validation/types`);
      return { status: "success", data: data.data };
    } catch(error) {
      console.error('Error fetching provider types:', error);
      return { status: "failed", data: [] };
    }
  }

  async exportProviderIPs(name) {
    try {
      const response = await axios.get(`${API_URL}/entities/providers/${name}/export`, {
        responseType: 'blob', // Important for file downloads
      });

      // Create blob and download link for gzipped file
      const blob = new Blob([response.data], { type: 'application/octet-stream' });
      const url = window.URL.createObjectURL(blob);

      // Create temporary download link
      const link = document.createElement('a');
      link.href = url;
      link.download = `${name.toLowerCase().replace(/\s+/g, '_')}_ips_export_${new Date().toISOString().slice(0, 19).replace(/:/g, '-')}.jsonl.gz`;

      // Trigger download
      document.body.appendChild(link);
      link.click();

      // Cleanup
      window.URL.revokeObjectURL(url);
      document.body.removeChild(link);

      return { status: "success", message: "Export started" };
    } catch(error) {
      if (error.response?.status == 429) {
        return { status: "ratelimited", message: "Too many requests" }
      } else if (error.response?.status == 401) {
        return { status: "unauthorized", message: "Not authorized" }
      } else if (error.response?.status == 404) {
        return { status: "not_found", message: "Provider not found" }
      } else {
        return { status: "failed", message: "Failed to export data" }
      }
    }
  }

  async submitProviderData(providerName, items, description = null, category = null) {
    try {
      const payload = {
        attribution: providerName,
        items: items,
      };

      if (description && description.trim() !== '') {
        payload.description = description.trim();
      }

      if (category !== null) {
        payload.category = category;
      }

      const { data } = await axios.post(
        `${API_URL}/submissions/data`,
        payload
      );

      return {
        status: "success",
        data: data
      };
    } catch(error) {
      if (error.response?.status === 429) {
        return { status: "ratelimited", message: "Too many requests" };
      } else if (error.response?.status === 401) {
        return { status: "unauthorized", message: "Not authorized" };
      } else if (error.response?.status === 404) {
        return { status: "not_found", message: "Provider not found" };
      } else if (error.response?.status === 400) {
        return {
          status: "validation_error",
          message: error.response.data.message || "Invalid data",
          details: error.response.data
        };
      } else {
        return { status: "failed", message: "Failed to submit data" };
      }
    }
  }

  async getCategories() {
    try {
      const { data } = await axios.get(`${API_URL}/entities/providers/categories`);
      return data.data;
    } catch(error) {
      if (error.response?.status === 429) {
        return { status: "ratelimited", data: [] }
      } else if (error.response?.status === 401) {
        return { status: "unauthorized", data: [] }
      } else {
        return { status: "failed", data: [] }
      }
    }
  }

  async getProviderCategories(name) {
    try {
      const { data } = await axios.get(`${API_URL}/entities/providers/${name}/categories`);
      return data.data;
    } catch(error) {
      if (error.response?.status === 429) {
        return { status: "ratelimited", data: [] }
      } else if (error.response?.status === 401) {
        return { status: "unauthorized", data: [] }
      } else if (error.response?.status === 404) {
        return { status: "not_found", data: [] }
      } else {
        return { status: "failed", data: [] }
      }
    }
  }

  async updateProviderCategories(name, categoryIds) {
    try {
      const { data } = await axios.put(`${API_URL}/entities/providers/${name}/categories`, {
        category_ids: categoryIds
      });
      return { status: "success", data: data };
    } catch(error) {
      if (error.response?.status === 429) {
        return { status: "ratelimited", message: "Too many requests" }
      } else if (error.response?.status === 401) {
        return { status: "unauthorized", message: "Not authorized" }
      } else if (error.response?.status === 404) {
        return { status: "not_found", message: "Provider not found" }
      } else if (error.response?.status === 400) {
        return { status: "validation_error", message: error.response.data.message || "Invalid category IDs" }
      } else {
        return { status: "failed", message: "Failed to update categories" }
      }
    }
  }

}

export default new Provider();
